import { Inter, JetBrains_Mono } from "@next/font/google";

export const inter = Inter({ subsets: ["latin"], preload: true });
export const mono = JetBrains_Mono({
  subsets: ["latin"],
  preload: true,
});

// monospace fonts are nice because we can do easy math on text strings.
// monospace fonts aren't nice, because they take extra room and can be harder to read.
type Weight = 400 | 500 | 600; // we limit ourselves to this subset of weights

export function getTextSize(text: string, px: number, weight: Weight) {
  // make sure each letter is calculated for the given weight
  [...text].forEach((char) => {
    if (!CHAR_MAP_10 || !CHAR_MAP_10[char] || !CHAR_MAP_10[char][weight]) {
      setChar10(char, weight);
    }
  });
  // sum the widths of each letter and the max height of the letters, scale'em
  const width = [...text].reduce(
    (sum, char) => sum + CHAR_MAP_10![char]![weight]![0],
    0
  );
  const height = [...text].reduce(
    (max, char) => Math.max(max, CHAR_MAP_10![char]![weight]![1]),
    0
  );
  const scale = px / 10;
  return {
    width: Number((width * scale).toFixed(2)),
    height: Number((height * scale).toFixed(2)),
  };
}

// This creates a map of char sizes for Inter.
//   - The standard keyboard map is precomputed by the developer
//   - Misses are calculated at run time

let setChar10_CANVAS: HTMLCanvasElement | undefined;
function setChar10(char: string, weight: Weight) {
  setChar10_CANVAS = setChar10_CANVAS || document.createElement("canvas");
  const context = setChar10_CANVAS.getContext("2d");
  if (!context) throw "no canvas context to work with";
  context.font = `${weight} 10px ${inter.style.fontFamily}`;
  var metrics = context.measureText(char);
  const height =
    metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  if (!CHAR_MAP_10) CHAR_MAP_10 = {}; // for typescript
  if (!CHAR_MAP_10[char])
    CHAR_MAP_10[char] = { 400: undefined, 500: undefined, 600: undefined };
  CHAR_MAP_10[char][weight] = [
    Number(metrics.width.toFixed(2)),
    Number(height.toFixed(2)),
  ];
}

// If you are working on this, uncomment this to capture the new prebuilt map
// function prebuildKeyboard10() {
//   if (CHAR_MAP_10) return;
//   CHAR_MAP_10 = {};
//   const keyboard =
//     "abcdefghijklmnopqrstuvwxyz" +
//     "ABCDEFGHIJKLMNOPQRSTUVWXYZ" +
//     "1234567890" +
//     "`~!@#$%^&*()_+-=" +
//     "[]{}|" +
//     ";':\"" +
//     ",./<>?" +
//     " ";
//   [...keyboard].forEach((char) => {
//     setChar10(char, 400);
//     setChar10(char, 500);
//     setChar10(char, 600);
//   });
// }
// setTimeout(() => {
//   prebuildKeyboard10();
//   console.log(JSON.stringify(CHAR_MAP_10));
// });

// capture and replace the output below
// a map of [width, height] of each character at 10px
let CHAR_MAP_10:
  | Record<string, Record<Weight, [number, number] | undefined>>
  | undefined = {
  "0": { "400": [6.25, 9], "500": [6.46, 9], "600": [6.67, 9] },
  "1": { "400": [4.65, 8], "500": [4.73, 8], "600": [4.81, 8] },
  "2": { "400": [6.05, 8], "500": [6.13, 8], "600": [6.21, 8] },
  "3": { "400": [6.36, 9], "500": [6.44, 9], "600": [6.52, 9] },
  "4": { "400": [6.42, 8], "500": [6.54, 8], "600": [6.66, 8] },
  "5": { "400": [6.08, 9], "500": [6.2, 9], "600": [6.32, 9] },
  "6": { "400": [6.24, 9], "500": [6.36, 9], "600": [6.48, 9] },
  "7": { "400": [5.71, 8], "500": [5.79, 8], "600": [5.87, 8] },
  "8": { "400": [6.16, 9], "500": [6.31, 9], "600": [6.46, 9] },
  "9": { "400": [6.24, 9], "500": [6.36, 9], "600": [6.48, 9] },
  a: { "400": [5.64, 7], "500": [5.69, 7], "600": [5.75, 7] },
  b: { "400": [6.21, 9], "500": [6.26, 9], "600": [6.3, 9] },
  c: { "400": [5.58, 7], "500": [5.68, 7], "600": [5.77, 7] },
  d: { "400": [6.21, 9], "500": [6.26, 9], "600": [6.3, 9] },
  e: { "400": [5.82, 7], "500": [5.87, 7], "600": [5.93, 7] },
  f: { "400": [3.61, 8], "500": [3.69, 8], "600": [3.77, 8] },
  g: { "400": [6.09, 9], "500": [6.17, 9], "600": [6.25, 9] },
  h: { "400": [5.91, 8], "500": [6.02, 8], "600": [6.13, 8] },
  i: { "400": [2.37, 8], "500": [2.49, 8], "600": [2.61, 8] },
  j: { "400": [2.37, 11], "500": [2.49, 11], "600": [2.61, 11] },
  k: { "400": [5.44, 8], "500": [5.56, 8], "600": [5.69, 8] },
  l: { "400": [2.37, 8], "500": [2.49, 8], "600": [2.61, 8] },
  m: { "400": [8.69, 6], "500": [8.84, 6], "600": [8.98, 6] },
  n: { "400": [5.85, 6], "500": [5.97, 6], "600": [6.1, 6] },
  o: { "400": [5.97, 7], "500": [6.02, 7], "600": [6.08, 7] },
  p: { "400": [6.09, 9], "500": [6.17, 9], "600": [6.25, 9] },
  q: { "400": [6.09, 9], "500": [6.17, 9], "600": [6.25, 9] },
  r: { "400": [3.72, 6], "500": [3.84, 6], "600": [3.97, 6] },
  s: { "400": [5.23, 7], "500": [5.36, 7], "600": [5.49, 7] },
  t: { "400": [3.64, 8], "500": [3.72, 8], "600": [3.8, 8] },
  u: { "400": [5.81, 7], "500": [5.94, 7], "600": [6.08, 7] },
  v: { "400": [5.57, 6], "500": [5.66, 6], "600": [5.76, 6] },
  w: { "400": [8.13, 6], "500": [8.25, 6], "600": [8.37, 6] },
  x: { "400": [5.4, 6], "500": [5.51, 6], "600": [5.63, 6] },
  y: { "400": [5.57, 9], "500": [5.66, 9], "600": [5.76, 9] },
  z: { "400": [5.41, 6], "500": [5.52, 6], "600": [5.62, 6] },
  A: { "400": [6.76, 8], "500": [7, 8], "600": [7.24, 8] },
  B: { "400": [6.51, 8], "500": [6.54, 8], "600": [6.57, 8] },
  C: { "400": [7.27, 9], "500": [7.35, 9], "600": [7.44, 9] },
  D: { "400": [7.19, 8], "500": [7.22, 8], "600": [7.24, 8] },
  E: { "400": [5.98, 8], "500": [6.03, 8], "600": [6.08, 8] },
  F: { "400": [5.87, 8], "500": [5.86, 8], "600": [5.86, 8] },
  G: { "400": [7.43, 9], "500": [7.49, 9], "600": [7.55, 9] },
  H: { "400": [7.4, 8], "500": [7.42, 8], "600": [7.44, 8] },
  I: { "400": [2.64, 8], "500": [2.7, 8], "600": [2.75, 8] },
  J: { "400": [5.43, 9], "500": [5.52, 9], "600": [5.61, 9] },
  K: { "400": [6.52, 8], "500": [6.64, 8], "600": [6.77, 8] },
  L: { "400": [5.63, 8], "500": [5.64, 8], "600": [5.66, 8] },
  M: { "400": [8.89, 8], "500": [8.98, 8], "600": [9.06, 8] },
  N: { "400": [7.53, 8], "500": [7.47, 8], "600": [7.41, 8] },
  O: { "400": [7.61, 9], "500": [7.68, 9], "600": [7.75, 9] },
  P: { "400": [6.35, 8], "500": [6.39, 8], "600": [6.43, 8] },
  Q: { "400": [7.61, 9], "500": [7.68, 9], "600": [7.76, 9] },
  R: { "400": [6.39, 8], "500": [6.45, 8], "600": [6.51, 8] },
  S: { "400": [6.38, 9], "500": [6.43, 9], "600": [6.49, 9] },
  T: { "400": [6.42, 8], "500": [6.51, 8], "600": [6.6, 8] },
  U: { "400": [7.41, 9], "500": [7.38, 9], "600": [7.34, 9] },
  V: { "400": [6.76, 8], "500": [7, 8], "600": [7.24, 8] },
  W: { "400": [9.49, 8], "500": [9.78, 8], "600": [10.07, 8] },
  X: { "400": [6.42, 8], "500": [6.66, 8], "600": [6.9, 8] },
  Y: { "400": [6.65, 8], "500": [6.85, 8], "600": [7.05, 8] },
  Z: { "400": [6.25, 8], "500": [6.39, 8], "600": [6.53, 8] },
  "`": { "400": [4.97, 2], "500": [4.97, 2], "600": [4.97, 2] },
  "~": { "400": [6.59, 3], "500": [6.66, 3], "600": [6.73, 3] },
  "!": { "400": [2.78, 9], "500": [2.94, 9], "600": [3.09, 9] },
  "@": { "400": [9.36, 9], "500": [9.68, 10], "600": [9.99, 11] },
  "#": { "400": [6.31, 8], "500": [6.37, 8], "600": [6.43, 8] },
  $: { "400": [6.38, 10], "500": [6.43, 10], "600": [6.49, 10] },
  "%": { "400": [8.13, 9], "500": [8.28, 9], "600": [8.44, 9] },
  "^": { "400": [4.69, 5], "500": [4.75, 5], "600": [4.81, 5] },
  "&": { "400": [6.39, 9], "500": [6.51, 9], "600": [6.63, 9] },
  "*": { "400": [5, 5], "500": [5.22, 5], "600": [5.43, 5] },
  "(": { "400": [3.62, 10], "500": [3.78, 10], "600": [3.93, 10] },
  ")": { "400": [3.62, 10], "500": [3.78, 10], "600": [3.93, 10] },
  _: { "400": [4.52, 1], "500": [4.6, 1], "600": [4.68, 2] },
  "+": { "400": [6.59, 6], "500": [6.66, 6], "600": [6.73, 6] },
  "-": { "400": [4.6, 2], "500": [4.63, 2], "600": [4.66, 2] },
  "=": { "400": [6.59, 4], "500": [6.66, 4], "600": [6.73, 4] },
  "[": { "400": [3.62, 10], "500": [3.78, 10], "600": [3.93, 10] },
  "]": { "400": [3.62, 10], "500": [3.78, 10], "600": [3.93, 10] },
  "{": { "400": [3.62, 10], "500": [3.78, 10], "600": [3.93, 10] },
  "}": { "400": [3.62, 10], "500": [3.78, 10], "600": [3.93, 10] },
  "|": { "400": [3.27, 13], "500": [3.42, 13], "600": [3.58, 13] },
  ";": { "400": [2.8, 8], "500": [2.88, 8], "600": [2.95, 8] },
  "'": { "400": [2.22, 4], "500": [2.17, 4], "600": [2.13, 4] },
  ":": { "400": [2.76, 7], "500": [2.83, 7], "600": [2.9, 7] },
  '"': { "400": [4.03, 4], "500": [4, 4], "600": [3.96, 4] },
  ",": { "400": [2.8, 3], "500": [2.88, 3], "600": [2.95, 3] },
  ".": { "400": [2.76, 3], "500": [2.83, 3], "600": [2.9, 3] },
  "/": { "400": [3.57, 10], "500": [3.68, 10], "600": [3.79, 10] },
  "<": { "400": [6.59, 6], "500": [6.66, 6], "600": [6.73, 6] },
  ">": { "400": [6.59, 6], "500": [6.66, 6], "600": [6.73, 6] },
  "?": { "400": [5.07, 9], "500": [5.26, 9], "600": [5.44, 9] },
  " ": { "400": [2.81, 0], "500": [2.65, 0], "600": [2.48, 0] },
};
